import { useState, type ClipboardEvent, type ReactNode } from 'react';
import type { MetricSourceRef } from '../api/useHypoteseKilder.ts';
import type { BaselineState, SourceClaim, ThresholdDirection, UpdateFrequency } from '../api/useHypotheses.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { tokenVar } from '../status/status.ts';
import { kildeMeta, kildestatus } from './kilder.ts';
import { Radiogruppe } from './deler.tsx';
import { delTallOgEnhet, lesTall, MAKS, type Felt, type LeddNoekkel, type Skjema } from './skjema.ts';

/**
 * Det åpne leddets felt (`hypotese-innsending.jsx`, AapentFelt). Etiketten er bare navnet;
 * grensene vises når de brytes (DD-44) — telleren fra 80 % av maks, i warning over den.
 * Terskelen er fire felt, fordi «Send inn» krever alle fire.
 */

const KILDEPAASTAND: Array<[SourceClaim, string]> = [
  ['avklart', 'Ja, og den er definert'],
  ['uklar', 'Ja, men definisjonen er uklar'],
  ['maa_bygges', 'Nei, den må bygges'],
];

const OPPDATERES: Array<[UpdateFrequency, string]> = [
  ['daglig', 'Daglig'],
  ['ukentlig', 'Ukentlig'],
  ['maanedlig', 'Månedlig'],
  ['kvartalsvis', 'Kvartalsvis'],
  ['sjeldnere', 'Sjeldnere'],
];

const BASELINE: Array<[BaselineState, string]> = [
  ['maalt', 'Målt'],
  ['ikke_maalt', 'Ikke målt ennå'],
];

const RETNING: Array<[ThresholdDirection, string]> = [
  ['minst', 'Minst'],
  ['hoyst', 'Høyst'],
];

export const frekvensTekst = (f: UpdateFrequency): string => OPPDATERES.find(([v]) => v === f)![1];
export const paastandTekst = (c: SourceClaim): string => KILDEPAASTAND.find(([v]) => v === c)![1];

/** Svaret på en lukket rad — helt, ikke forkortet (DD-67). `null` når leddet ikke er påbegynt. */
export function svarFor(k: LeddNoekkel, s: Skjema, kilde?: MetricSourceRef): string | null {
  // H3.3: kilden fra katalogen står med navnet og det katalogen sier om den.
  if (k === 'kilde' && s.metricSourceId) {
    return kilde ? `${kilde.name} · fra kildelisten · oppdateres ${frekvensTekst(kilde.updateFrequency).toLowerCase()} · ${kildestatus(kilde.status).etikett.toLowerCase()}` : 'Fra kildelisten';
  }
  const ledd = (...deler: Array<string | undefined | false>) => deler.filter((d): d is string => Boolean(d && d.trim())).join(' · ') || null;
  switch (k) {
    case 'tittel':
      return ledd(s.title);
    case 'endring':
      return ledd(s.change, s.audience);
    case 'effekt':
      return ledd(s.effect);
    case 'maaltall':
      return ledd(s.metricName);
    case 'kilde':
      return ledd(s.sourceText, s.updateFrequency && `oppdateres ${OPPDATERES.find(([v]) => v === s.updateFrequency)![1].toLowerCase()}`, s.sourceClaim && KILDEPAASTAND.find(([v]) => v === s.sourceClaim)![1].toLowerCase());
    case 'idag':
      return ledd(s.baselineState === 'ikke_maalt' ? 'ikke målt ennå' : s.baselineValue && `${s.baselineValue}${s.unit.trim() ? ` ${s.unit.trim()}` : ''}`, s.baselineOn && `per ${datoTekst(s.baselineOn)}`);
    case 'rett':
      return ledd(s.threshold && `${s.direction === 'hoyst' ? 'høyst' : s.direction === 'minst' ? 'minst' : ''} ${s.threshold}${s.unit.trim() ? ` ${s.unit.trim()}` : ''}`.trim(), s.testEndsOn && `innen ${datoTekst(s.testEndsOn)}`);
    case 'venter':
      return ledd(s.whatWeLose);
    case 'hvorfor':
      return ledd(s.background);
  }
}

const MND = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
const datoTekst = (iso: string) => {
  const [aa, mm, dd] = iso.split('-');
  return aa && mm && dd ? `${Number(dd)}. ${MND[Number(mm) - 1]} ${aa}` : iso;
};

/** Et felt med etikett, teller nær grensen, og en linje under når noe må gjøres. */
function Felt({ etikett, id, felt, verdi, children, under }: { etikett: string; id: string; felt?: Felt; verdi?: string; children: ReactNode; under?: ReactNode }) {
  const maks = felt ? MAKS[felt] : undefined;
  const lengde = verdi?.length ?? 0;
  const vis = maks !== undefined && lengde >= maks * 0.8;
  const over = maks !== undefined && lengde > maks;
  return (
    <div className="h-felt">
      <span className="h-felt-hode">
        <label htmlFor={id} className="h-etikett">
          {etikett}
        </label>
        {vis ? (
          <span className={over ? 'h-teller h-teller-over' : 'h-teller'} id={`${id}-teller`}>
            {over ? <Ikon navn="warning-outline" className="h-teller-ikon" /> : null}
            {lengde} / {maks}
          </span>
        ) : null}
      </span>
      {children}
      {over ? (
        <span className="h-felt-grunn" id={`${id}-grunn`}>
          <Ikon navn="warning-outline" className="h-varsel-ikon-warning" />
          {lengde - maks!} tegn for mye. Teksten står, men utkastet lagres ikke før den er kortere.
        </span>
      ) : null}
      {under}
    </div>
  );
}

export function LeddFelt({
  k,
  s,
  oppdater,
  spId,
  kilde,
  tenant,
}: {
  k: LeddNoekkel;
  s: Skjema;
  oppdater: (patch: Partial<Skjema>) => void;
  spId: string;
  /** H3.3: kilden fra katalogen utkastet bruker, til det låste kortet i kildeleddet (W7). */
  kilde?: MetricSourceRef;
  tenant?: string;
}) {
  const smal = useSmal();
  const [limt, setLimt] = useState<string | null>(null);
  const tekst = (felt: Felt, id: string, rader: number, etikett?: string, plassholder?: string) => {
    const verdi = String(s[felt] ?? '');
    const over = (MAKS[felt] ?? Infinity) < verdi.length;
    const input =
      rader > 1 ? (
        <textarea id={id} className="h-input h-tekstfelt" rows={smal ? rader + 2 : rader} value={verdi} aria-invalid={over || undefined} aria-describedby={over ? `${id}-grunn` : undefined} onChange={(e) => oppdater({ [felt]: e.target.value })} data-testid={`h-${felt}`} />
      ) : (
        <input id={id} className="h-input" value={verdi} placeholder={plassholder} aria-invalid={over || undefined} aria-describedby={over ? `${id}-grunn` : undefined} onChange={(e) => oppdater({ [felt]: e.target.value })} data-testid={`h-${felt}`} />
      );
    return etikett ? (
      <Felt etikett={etikett} id={id} felt={felt} verdi={verdi}>
        {input}
      </Felt>
    ) : (
      input
    );
  };

  switch (k) {
    case 'tittel':
      return <input id="f-tittel" className="h-stort" aria-labelledby={spId} value={s.title} onChange={(e) => oppdater({ title: e.target.value })} data-testid="h-title" />;
    case 'maaltall':
      return <input id="f-maal" className="h-stort" aria-labelledby={spId} value={s.metricName} onChange={(e) => oppdater({ metricName: e.target.value })} data-testid="h-metricName" />;
    case 'endring':
      return (
        <div className="h-felter-kolonne">
          {tekst('change', 'f-endring', 3, 'Endringen')}
          {tekst('audience', 'f-hvem', 1, 'For hvem')}
        </div>
      );
    case 'effekt':
      return tekst('effect', 'f-effekt', 5, 'Effekten');
    case 'venter':
      return tekst('whatWeLose', 'f-venter', 4, 'Det vi mister');
    case 'hvorfor':
      return tekst('background', 'f-hvorfor', 5, 'Bakgrunnen');
    case 'kilde':
      if (s.metricSourceId) {
        // W7: katalogen svarte — kilden er et låst kort; status og oppdatering følger kilden.
        const st = kilde ? kildestatus(kilde.status) : null;
        return (
          <div className="h-kilde-valgt" data-testid="h-kilde-valgt">
            <p className="h-kilde-valgt-navn">
              <Ikon navn="server-outline" />
              <b>{kilde?.name ?? 'Kilden fra kildelisten'}</b>
              <span className="hb-liten">fra kildelisten</span>
            </p>
            {kilde ? <p className="h-kilde-valgt-maaler">{kilde.measures}</p> : null}
            {kilde && st ? (
              <p className="h-kildekort-meta">
                <span className="h-kildestatus" style={{ color: tokenVar(st.fg) }}>
                  <Ikon navn={st.ikon} />
                </span>
                {kildeMeta(kilde)}
              </p>
            ) : null}
            <p className="h-kilde-valgt-fot">
              <span className="hb-dempet">Status og oppdatering følger kilden.</span>
              <button type="button" className="panel-knapp-lenke" onClick={() => oppdater({ metricSourceId: undefined })}>
                Bytt kilde
              </button>
            </p>
          </div>
        );
      }
      return (
        <div className="h-felter-kolonne">
          {tekst('sourceText', 'f-kilde', 1, 'Kilden', tenant ? `Skriv kilden, eller velg fra kildene hos ${tenant} →` : undefined)}
          <div className="h-felt">
            <span id="f-paastand-h" className="h-etikett">
              Finnes tallet der?
            </span>
            <Radiogruppe etikettId="f-paastand-h" valg={KILDEPAASTAND} aktiv={s.sourceClaim} onVelg={(v) => oppdater({ sourceClaim: v })} testid="h-sourceClaim" />
          </div>
          <div className="h-felt">
            <span id="f-frekvens-h" className="h-etikett">
              Oppdateres
            </span>
            <Radiogruppe etikettId="f-frekvens-h" valg={OPPDATERES} aktiv={s.updateFrequency} onVelg={(v) => oppdater({ updateFrequency: v })} testid="h-updateFrequency" />
          </div>
        </div>
      );
    case 'idag': {
      /* Lim inn fra en annen fane: «41,3 %» deles i tallet og enheten NÅR enheten er tom —
         sagt i én linje, aldri stille. Annet enn tall og enhet røres ikke. */
      const lim = (e: ClipboardEvent<HTMLInputElement>) => {
        const delt = delTallOgEnhet(e.clipboardData.getData('text/plain'));
        if (!delt || s.unit.trim()) return;
        e.preventDefault();
        oppdater({ baselineValue: delt.tall, unit: delt.enhet });
        setLimt(`${delt.tall} ${delt.enhet}`);
      };
      const ugyldig = s.baselineValue.trim() !== '' && lesTall(s.baselineValue) === undefined;
      return (
        <div className="h-felter-kolonne">
          <div className="h-felt">
            <span id="f-baseline-h" className="h-etikett">
              Er tallet målt?
            </span>
            <Radiogruppe etikettId="f-baseline-h" valg={BASELINE} aktiv={s.baselineState} onVelg={(v) => oppdater({ baselineState: v })} testid="h-baselineState" />
          </div>
          <div className="h-felter-rad">
            {s.baselineState !== 'ikke_maalt' ? (
              <Felt etikett="Tallet" id="f-idag" under={ugyldig ? <span className="h-felt-grunn">Må være et tall, f.eks. 41 eller 41,3.</span> : null}>
                <span className="h-tall-med-enhet">
                  <input id="f-idag" className="h-input h-input-tall" inputMode="decimal" value={s.baselineValue} aria-invalid={ugyldig || undefined} onPaste={lim} onChange={(e) => oppdater({ baselineValue: e.target.value })} data-testid="h-baselineValue" />
                  {s.unit.trim() ? <span className="h-enhet">{s.unit.trim()}</span> : null}
                </span>
              </Felt>
            ) : null}
            <Felt etikett={s.baselineState === 'ikke_maalt' ? 'Per' : 'Målt'} id="f-idag-dato">
              <input id="f-idag-dato" type="date" className="h-input h-input-dato" value={s.baselineOn} onChange={(e) => oppdater({ baselineOn: e.target.value })} data-testid="h-baselineOn" />
            </Felt>
          </div>
          {limt ? (
            <p className="h-hjelp" role="status">
              <Ikon navn="information-circle-outline" className="h-hjelp-ikon" />
              Du limte inn «{limt}». Enheten er satt til «{s.unit.trim()}».
            </p>
          ) : null}
        </div>
      );
    }
    case 'rett': {
      const ugyldig = s.threshold.trim() !== '' && lesTall(s.threshold) === undefined;
      return (
        <div className="h-felter-kolonne">
          <div className="h-felter-rad">
            <div className="h-felt">
              <span id="f-retning-h" className="h-etikett">
                Retning
              </span>
              <Radiogruppe etikettId="f-retning-h" valg={RETNING} aktiv={s.direction} onVelg={(v) => oppdater({ direction: v })} testid="h-direction" />
            </div>
            <Felt etikett="Tallet" id="f-terskel" under={ugyldig ? <span className="h-felt-grunn">Må være et tall, f.eks. 46 eller 2,5.</span> : null}>
              <input id="f-terskel" className="h-input h-input-tall" inputMode="decimal" value={s.threshold} aria-invalid={ugyldig || undefined} onChange={(e) => oppdater({ threshold: e.target.value })} data-testid="h-threshold" />
            </Felt>
            <Felt etikett="Enhet" id="f-enhet" felt="unit" verdi={s.unit}>
              <input id="f-enhet" className="h-input h-input-enhet" value={s.unit} onChange={(e) => oppdater({ unit: e.target.value })} data-testid="h-unit" />
            </Felt>
            <Felt etikett="Innen" id="f-innen">
              <input id="f-innen" type="date" className="h-input h-input-dato" value={s.testEndsOn} onChange={(e) => oppdater({ testEndsOn: e.target.value })} data-testid="h-testEndsOn" />
            </Felt>
          </div>
          {smal ? null : (
            <p className="h-tabhint" aria-hidden="true">
              <kbd>Tab</kbd> neste felt · <kbd>Shift</kbd> <kbd>Tab</kbd> forrige
            </p>
          )}
        </div>
      );
    }
  }
}
